import { useCallback, useEffect, useMemo, useState } from 'react';
import { Empty, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { FormikProps } from 'formik';
import { groupBy } from 'lodash';
import { useOutletContext, useParams } from 'react-router-dom';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import Notification from 'components/ui/base/notification/Notification';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import useDeclarations from 'hooks/useDeclarations';
import useFormUtils from 'hooks/useFormUtils';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useProducts from 'hooks/useProducts';
import { ICS2ConsignmentHouseLevel, ICS2GoodsItemResponse } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { getErrorMessage } from 'utils/getErrorMessage';
import HouseConsignmentListItem from './HouseConsignmentListItem';

/**
 * House consignments have no endpoints of their own: they are stored with the declaration and are
 * created, updated and deleted through the declaration save. So the list is read straight off the
 * master form, which also keeps edits that have not been saved yet visible here.
 */
const HouseConsignmentsPage = () => {
    const { formik, viewOnly, houseGoodsItems, refetchHouseGoodsItems, onCreateHouseConsignmentProduct } =
        useHouseConsignmentsContext();
    const { declarationId } = useParams();
    const { countryLowerCase: country, formType, internalType } = useFormUtils();
    const { declaration, deleteEnsIcs2HouseConsignment } = useDeclarations();
    const { deleteDeclarationProduct } = useProducts();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { declarationErrors } = useDeclarationFormErrors();

    // The parser numbers the consignments and their items by their place in the declaration, which
    // is the order they are listed in here.
    const errorsByConsignmentNumber = useMemo(
        () => new Map((declarationErrors.houseConsignments ?? []).map((entry) => [entry.index, entry])),
        [declarationErrors.houseConsignments]
    );

    // The items come back for the whole declaration, so each card gets the ones belonging to it.
    const itemsByConsignment = useMemo(() => groupBy(houseGoodsItems ?? [], 'houseConsignmentId'), [houseGoodsItems]);

    // Coming back from an item form, the items may have changed while this page was not mounted.
    useEffect(() => {
        refetchHouseGoodsItems?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const houseConsignments: ICS2ConsignmentHouseLevel[] = useMemo(
        () => formik?.values?.consignmentMasterLevel?.consignmentHouseLevel ?? [],
        [formik?.values]
    );

    const handleDeleteItem = useCallback(
        async (productId: string) => {
            if (!declarationId) return;

            await deleteDeclarationProduct({ productId, declarationId, formType, internalType, country });
            await refetchHouseGoodsItems?.();
        },
        [country, declarationId, deleteDeclarationProduct, formType, internalType, refetchHouseGoodsItems]
    );

    /**
     * The consignment awaiting confirmation, by id and by its place in the list - deleting one takes
     * the goods items it holds with it, so it is confirmed before the save runs.
     */
    const [pendingDeletion, setPendingDeletion] = useState<{ id: string; consignmentNumber: number } | null>(null);

    const requestDelete = useCallback(
        async (houseConsignmentId: string) => {
            const index = houseConsignments.findIndex(({ id }) => id === houseConsignmentId);
            if (index === -1) return;

            setPendingDeletion({ id: houseConsignmentId, consignmentNumber: index + 1 });
        },
        [houseConsignments]
    );

    const handleDelete = useCallback(async () => {
        const houseConsignmentId = pendingDeletion?.id;

        setPendingDeletion(null);

        if (!(declarationId && declaration && houseConsignmentId)) return;

        // Blocks input while the save runs - a second click would send the house list this one
        // is about to change, putting the deleted consignment back.
        showGlobalOverlay({ type: 'LoadingOverlay' });

        try {
            await deleteEnsIcs2HouseConsignment(declarationId, declaration, houseConsignmentId);
            await refetchHouseGoodsItems?.();
        } catch (error) {
            Notification({
                type: 'error',
                messageTitle: 'Deleting the house consignment failed.',
                description: getErrorMessage(error) || 'Something went wrong.',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    }, [
        declaration,
        declarationId,
        deleteEnsIcs2HouseConsignment,
        hideGlobalOverlay,
        pendingDeletion,
        refetchHouseGoodsItems,
        showGlobalOverlay,
    ]);

    if (!houseConsignments.length) return <Empty description="No house consignments added yet." />;

    const pendingItemCount = pendingDeletion ? (itemsByConsignment[pendingDeletion.id] ?? []).length : 0;

    return (
        <>
            <Space direction="vertical" size={16} style={{ width: '100%', overflowY: 'scroll', height: '100%' }}>
                {houseConsignments.map((houseConsignment, index) => {
                    const errors = errorsByConsignmentNumber.get(index + 1);

                    return (
                        <HouseConsignmentListItem
                            key={houseConsignment.id ?? index}
                            data={houseConsignment}
                            consignmentNumber={index + 1}
                            viewOnly={viewOnly}
                            items={itemsByConsignment[houseConsignment.id ?? ''] ?? []}
                            hasErrors={Boolean(errors?.errors?.length)}
                            invalidItemNumbers={new Set((errors?.items ?? []).map(({ index: itemIndex }) => itemIndex))}
                            onDelete={requestDelete}
                            onAddItem={onCreateHouseConsignmentProduct}
                            onDeleteItem={handleDeleteItem}
                        />
                    );
                })}
            </Space>

            <ConfirmationModal
                width={632}
                type="error"
                title={`Delete House Consignment ${pendingDeletion?.consignmentNumber}?`}
                confirmButtonText="Delete"
                icon={<DeleteOutlined />}
                content={
                    <Typography.Text>
                        The house consignment will be deleted
                        {pendingItemCount > 0
                            ? `, along with the ${pendingItemCount} goods ${
                                  pendingItemCount === 1 ? 'item' : 'items'
                              } it holds`
                            : ''}
                        , and all information associated with it will be lost.
                    </Typography.Text>
                }
                open={Boolean(pendingDeletion)}
                onCancel={() => setPendingDeletion(null)}
                onConfirm={handleDelete}
            />
        </>
    );
};

export default HouseConsignmentsPage;

function useHouseConsignmentsContext() {
    return useOutletContext<{
        formik: FormikProps<any>;
        onEdit: (id: string) => Promise<void>;
        loading?: boolean;
        viewOnly?: boolean;
        houseGoodsItems?: ICS2GoodsItemResponse[];
        refetchHouseGoodsItems?: () => Promise<void>;
        onCreateHouseConsignmentProduct?: (houseConsignmentId: string) => Promise<void>;
    }>();
}
